import { Injectable, inject } from '@angular/core';
import type {
  ActivityEvaluationRequest,
  ActivityEvaluationResult,
} from '../../models/interfaces/activity-evaluation.interface';
import { Api } from '../api/api';

export const ACTIVITY_SCORE_SCALE = 10;
export const ACTIVITY_MINIMUM_SCORE = 7;

interface ActivityAttemptResponse {
  contentId: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  approved: boolean;
  result: string;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class ActivityEvaluationService {

  private readonly api = inject(Api);

  private readonly results = new Map<string, ActivityEvaluationResult>();

  public async evaluate(request: ActivityEvaluationRequest): Promise<ActivityEvaluationResult> {
    const answers = [...request.answersByQuestionId.entries()].map(([questionId, optionIndex]) => ({
      questionId,
      optionIndex,
    }));
    const response = await this.api.postAsync<ActivityAttemptResponse>(
      `/contents/${request.contentId}/activity/attempts`,
      { answers },
    );

    if (response.error) {
      throw response.error;
    }

    if (!response.success) {
      throw new Error('Não foi possível corrigir a atividade.');
    }

    const result = this.toResult(request.userId, response.success);
    this.remember(result);
    return result;
  }

  public saveResult(result: ActivityEvaluationResult): void {
    this.remember(result);
  }

  public getSavedResult(userId: string, contentId: string): ActivityEvaluationResult | null {
    return this.results.get(this.getStorageKey(contentId, userId)) ?? null;
  }

  public async loadLatest(userId: string, contentId: string): Promise<ActivityEvaluationResult | null> {
    const response = await this.api.getAsync<ActivityAttemptResponse>(
      `/contents/${contentId}/activity/attempts/latest`,
    );

    if (response.error) {
      if (response.error.status === 404) {
        return null;
      }

      throw response.error;
    }

    if (!response.success) {
      return null;
    }

    const result = this.toResult(userId, response.success);
    this.remember(result);
    return result;
  }

  public clearSavedResult(userId: string, contentId: string): void {
    this.results.delete(this.getStorageKey(contentId, userId));
  }

  private remember(result: ActivityEvaluationResult): void {
    this.results.set(this.getStorageKey(result.contentId, result.userId), result);
  }

  private getStorageKey(contentId: string, userId: string): string {
    return `${contentId}-${userId}`;
  }

  private toResult(userId: string, attempt: ActivityAttemptResponse): ActivityEvaluationResult {
    return {
      userId,
      contentId: attempt.contentId,
      totalQuestions: attempt.totalQuestions,
      minimumScore: ACTIVITY_MINIMUM_SCORE,
      totalCorrect: attempt.score,
      userScore: attempt.percentage / (100 / ACTIVITY_SCORE_SCALE),
      approved: attempt.approved,
      correctQuestions: [],
      completedAt: new Date().toISOString(),
      percentage: attempt.percentage,
      result: attempt.result,
      message: attempt.message,
    };
  }
}
